import * as React from "react";
import { Button, Form, Modal } from "semantic-ui-react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";

import { Input } from "@neworbit/simpleui-input";

import { CaseType } from "@teamprevent/common/model";

import { routeIdSelector as customerIdSelector } from "../../../../../crud/customer/selectors";
import { routeIdSelector as employeeRecordIdSelector } from "../../../../../crud/employeeRecord/selectors";
import { routeIdSelector as ohCaseIdSelector } from "../../../../../crud/ohCase/selectors";
import { PartialOhCaseDetailModel } from "../../../model";
import { ReopenHealthSurveillanceCaseModel } from "../../../../../healthSurveillance/models";
import { ohCaseApi } from "../../..";

import { push } from "redux-little-router";

interface Props {
    ohCase: PartialOhCaseDetailModel;
}

export const ReopenHealthSurveillanceCaseButton: React.FunctionComponent<Props> = (props: Props) => {

    const dispatch = useDispatch();

    const [openModal, setOpenModal] = React.useState<boolean>(false);
    const [reason, setReason] = React.useState<string>(null);
    const [submitted, setSubmitted] = React.useState<boolean>(false);
    const [saving, setSaving] = React.useState<boolean>(false);

    const customerId = useSelector(customerIdSelector);
    const employeeRecordId = useSelector(employeeRecordIdSelector);
    const ohCaseId = useSelector(ohCaseIdSelector);

    const isHealthSurveillanceCase = props.ohCase.caseType === CaseType.HEALTH_SURVEILLANCE;
    const isCancelledOrClosed = props.ohCase.caseStatus === "Cancelled" || props.ohCase.caseStatus === "Closed";

    const resetState = () => {
        setOpenModal(false);
        setReason(null);
        setSubmitted(false);
        setSaving(false);
    };

    const onOpenModal = React.useCallback(() => {
        setOpenModal(true);
    }, []);

    const onCloseModal = React.useCallback(() => {
        setOpenModal(false);
    }, []);

    const onConfirm = React.useCallback(async () => {
        setSubmitted(true);

        if (!reason) {
            return;
        }

        setSaving(true);
        const model: ReopenHealthSurveillanceCaseModel = { reason };
        const newOhCase = await ohCaseApi.reopenHealthSurveillanceCase(customerId, employeeRecordId, ohCaseId, model);
        setSaving(false);

        dispatch(push(`/customers/${customerId}/employeeRecords/${employeeRecordId}/ohCases/${newOhCase.id}`));

        resetState();
    }, [dispatch, customerId, employeeRecordId, ohCaseId, reason]);

    if (!isHealthSurveillanceCase || !isCancelledOrClosed) {
        return null;
    }

    return (<>
        <Modal size="tiny" open={openModal}>
            <Modal.Header content="Reopen Case" />
            <Modal.Content>
                <p>
                    You are able to reopen a case which has been Closed/Cancelled, in doing so,
                    you are creating a new case which contains all the medicals which were associated to this case.
                    Do you wish to continue?
                </p>
                <Form>
                    <Form.Field required>
                        <Input.Textarea
                            placeholder={"Reason..."}
                            required
                            value={reason}
                            showErrors={submitted}
                            label="Reason"
                            onChange={setReason}
                            rows={5}
                        />
                    </Form.Field>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button content="Confirm" onClick={onConfirm} disabled={saving} />
                <Button content="Cancel" onClick={onCloseModal} disabled={saving} />
            </Modal.Actions>
        </Modal>
        <Button onClick={onOpenModal}>Reopen Case</Button>
    </>);
};
